import React from 'react'
import { useTranslation } from 'react-i18next'
import { Route, Outlet, useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@apollo/client'

import { OPERATION_IS_STIME_RAN, OPERATION_SSID_CONTEXT } from './operations-gql/queries'
import { canSeeStimeFields } from './permissions'
import useRemainingKeys from './use-remaining-keys'

import RouteGuard from 'components/AuthenticatedLayout/RouteGuard/RouteGuard'
import { Navigate } from 'router'
import useSubscribeRefresh from 'use-subscribe-actions'
import { useSites, useHasSitesOnProduction, useUser } from 'store'
import EmptyScreen from 'packages/ui/EmptyScreen'

import Ran from './Ran'
import RanNotEnabled from './RanNotEnabled'
import RanExport from './Actions/Export'
import Delete from './Actions/Delete'
import Reset from './Actions/Reset'
import Import from './Actions/Import'
import Card from './Card/Card'
import { default as FormCreate, FormUpdate } from './Form'

import Loading from 'components/global/Loading'

const Layout = () => {
  const { t } = useTranslation()
  const hasSitesOnProduction = useHasSitesOnProduction()
  const navigate = useNavigate()
  useSubscribeRefresh(() => navigate('/ran'))
  return (
    <RouteGuard requiredRoles={['ran', 'ran_stime', 'ran_stime_ro']}>
      {hasSitesOnProduction ? (
        <Outlet />
      ) : (
        <EmptyScreen icon="square-poll-vertical">{t('ui_elements.service_soon')}</EmptyScreen>
      )}
    </RouteGuard>
  )
}

const Redirect = () => {
  const [{ selected }] = useSites()
  const { t } = useTranslation()
  if (selected.length !== 1) {
    return <EmptyScreen icon="square-poll-vertical">{t('ran.no_site_selected')}</EmptyScreen>
  }
  return <Navigate pathname={selected[0].sf_id} />
}

const WithStime = () => {
  const [user] = useUser()
  const { siteId } = useParams()
  const { data: isStimeData, loading: isStimeLoading } = useQuery(OPERATION_IS_STIME_RAN, {
    variables: { sf_id: siteId },
    skip: !siteId,
    errorPolicy: 'all'
  })
  const shouldFetchSsidContext = Boolean(isStimeData?.is_stime) && canSeeStimeFields(user)
  const { data: contextData, loading: contextLoading } = useQuery(OPERATION_SSID_CONTEXT, {
    variables: { sf_id: siteId },
    skip: !shouldFetchSsidContext,
    errorPolicy: 'all'
  })
  const isInitialLoading = (isStimeLoading && !isStimeData) || (contextLoading && !contextData)
  const remainingKeys = useRemainingKeys({
    siteId,
    ssids: shouldFetchSsidContext ? contextData?.ssids : undefined,
    skip: isInitialLoading
  })

  if (isInitialLoading) {
    return <Loading />
  }
  return <Outlet context={{ isStime: isStimeData?.is_stime, ...contextData, remainingKeys }} />
}

export const RanRoutes = ({ path = '/ran' }) => [
  <Route path={path} key="ran" element={<Layout />}>
    <Route path="" index key="ran-empty" element={<Redirect />} />
    <Route path="not-enabled" element={<RanNotEnabled />} />
    <Route path=":siteId" key="ran-site" element={<WithStime />}>
      <Route key="ran-site-content" element={<Ran />}>
        <Route index key="ran-site-index" element={null} />
        <Route path="new" key="ran-new" element={<FormCreate />} />
        <Route path="export" key="ran-export" element={<RanExport />} />
        <Route path="import" key="ran-import" element={<Import />} />
        <Route path=":roomId" key="ran-card" element={<Card />}>
          <Route path="new" key="ran-card-new" element={<FormCreate />} />
          <Route path="edit" key="ran-card-edit" element={<FormUpdate />} />
          <Route path="export" key="ran-card-export" element={<RanExport />} />
          <Route path="delete" key="ran-card-delete" element={<Delete />} />
          <Route path="reset" key="ran-card-reset" element={<Reset />} />
          <Route path="import" key="ran-card-import" element={<Import />} />
        </Route>
      </Route>
    </Route>
    <Route path="not-enabled" element={<RanNotEnabled />} />
  </Route>
]

export default RanRoutes
